import React from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import PgIcon from './pg-icon';

const PgNavItem = ({
    icon,
    label,
    href,
    active,
    hasSubmenu,
    actionIcons,
    onClick,
    title,
    className,
    ...props
}) => {
    const classes = [
        'pg-nav-item',
        active && 'is-active',
        hasSubmenu && 'has-submenu',
        className,
    ].filter(Boolean).join(' ');

    const content = (
        <>
            {icon && (
                <span className="pg-nav-icon">
                    <PgIcon name={icon} size={18} />
                </span>
            )}
            <span className="pg-nav-label">{label}</span>
            {actionIcons.length > 0 && (
                <span className="pg-nav-actions">
                    {actionIcons.map((action) => (
                        <button
                            key={action.id || action.icon}
                            type="button"
                            className="pg-nav-action"
                            title={action.label}
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                action.onClick?.(e);
                            }}
                        >
                            <PgIcon name={action.icon} size={14} />
                        </button>
                    ))}
                </span>
            )}
            {hasSubmenu && (
                <span className="pg-nav-arrow">
                    <PgIcon name="chevron-right" size={12} />
                </span>
            )}
        </>
    );

    const resolvedTitle = title || (typeof label === 'string' ? label : undefined);

    if (href) {
        return (
            <Link href={href} className={classes} title={resolvedTitle} onClick={onClick} {...props}>
                {content}
            </Link>
        );
    }

    return (
        <button type="button" className={classes} title={resolvedTitle} onClick={onClick} {...props}>
            {content}
        </button>
    );
};

PgNavItem.propTypes = {
    /** Tabler icon slug (see `PgIcon`), e.g. `"home"`, `"search"`. */
    icon: PropTypes.string,
    label: PropTypes.node.isRequired,
    /** Renders a `next/link` instead of a `<button>` when set. */
    href: PropTypes.string,
    /** Highlighted state via `.is-active`. */
    active: PropTypes.bool,
    /** Trailing chevron via `.has-submenu`, and switches the row to space-between layout. Purely visual — it does not open anything itself. */
    hasSubmenu: PropTypes.bool,
    /** Icons revealed on hover at the right edge, e.g. an "add" or "more" action. Each click stops propagation so it doesn't also trigger the row's own `onClick`. */
    actionIcons: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        icon: PropTypes.string.isRequired,
        label: PropTypes.string,
        onClick: PropTypes.func,
    })),
    onClick: PropTypes.func,
    /** Native `title` attribute — also what the collapsed sidebar's CSS tooltip (`content: attr(title)`) reads. Defaults to `label` when `label` is a plain string. */
    title: PropTypes.string,
    className: PropTypes.string,
};

PgNavItem.defaultProps = {
    icon: '',
    href: undefined,
    active: false,
    hasSubmenu: false,
    actionIcons: [],
    onClick: undefined,
    title: '',
    className: '',
};

export default PgNavItem;
